export const REVENUE_LAND_CASE_TAXONOMY_CONFIG = {
  id: 'revenue_land',
  label: 'Revenue / Land',
  shortLabel: 'Revenue',
  description: 'Mutation, partition, correction of revenue record, demarcation, possession, revenue appeals, revisions, Board of Revenue matters, land acquisition, and related land proceedings.',
  active: true,
  types: [
    {
      id: 'Mutation Matter',
      label: 'Mutation Matter',
      shortLabel: 'Mutation',
      description: 'Proceedings relating to attestation, cancellation, correction, challenge, inheritance, sale, gift, exchange, oral gift/hiba, decree-based, or registered-deed-based mutation in revenue record.',
      legalBasis: [
        'Land Revenue Act framework.',
        'Section 42 Land Revenue Act context for reporting/acquisition of rights and mutation entries, where applicable.',
        'Mutation is generally a fiscal/revenue entry and may not by itself create title.',
        'Title disputes may require civil court adjudication.',
        'Fraud/misrepresentation/title disputes may require civil suit/writ/revenue remedy depending facts.'
      ],
      subtypes: [
        'Attestation of Mutation',
        'Challenge to Mutation',
        'Cancellation of Mutation',
        'Correction of Mutation',
        'Inheritance Mutation',
        'Sale Mutation',
        'Gift Mutation',
        'Exchange Mutation',
        'Oral Gift / Hiba Mutation',
        'Mutation Based on Decree',
        'Mutation Based on Registered Deed',
        'Mutation Fraud / Misrepresentation',
        'Other Mutation Matter',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Tehsildar / Naib Tehsildar',
          label: 'Tehsildar / Naib Tehsildar',
          description: 'Common revenue authority for mutation proceedings depending on local setup.'
        },
        {
          id: 'Assistant Collector',
          label: 'Assistant Collector',
          description: 'Revenue forum for mutation disputes/attestation/correction where applicable.'
        },
        {
          id: 'Collector',
          label: 'Collector',
          description: 'Higher revenue authority for appeal/revision/administrative correction depending on law/practice.'
        },
        {
          id: 'Board of Revenue',
          label: 'Board of Revenue',
          description: 'Use for higher-level revenue revision/review/administrative matters where applicable.'
        },
        {
          id: 'Service Delivery Centre / Land Record Centre',
          label: 'Service Delivery Centre / Land Record Centre',
          description: 'Use for administrative mutation entry/fard/correction process where applicable.'
        },
        {
          id: 'Civil Court',
          label: 'Civil Court',
          description: 'Use where the real dispute concerns title, fraud, declaration, or civil rights.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where revenue authority order is challenged by writ where maintainable.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Mutation forum depends on whether the matter is simple entry/attestation, disputed mutation, appeal/revision, fraud/title dispute, or writ challenge.',
      sideOptions: ['Applicant', 'Respondent', 'Petitioner', 'Objector', 'Landowner', 'Legal Heir', 'Purchaser', 'Vendor'],
      documentTabs: [
        'All',
        'Mutation Application / Petition',
        'Revenue Record',
        'Deed / Title Documents',
        'Objections / Replies',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Record / Fard Review',
        'Mutation Application / Objection Preparation',
        'Filing / Submission before Revenue Authority',
        'Notice to Interested Parties, if required',
        'Revenue Field Report / Patwari Report',
        'Hearing before Revenue Officer',
        'Evidence / Documents Review',
        'Attestation / Rejection / Correction Order',
        'Appeal / Revision / Civil Remedy, if any',
        'Implementation in Record',
        'Closed'
      ],
      aiContextHints: 'AI should understand mutation number, mutation type, deed/decree/inheritance basis, parties, land record, khasra/khewat/khata, title dispute, objections, revenue officer order, and implementation.'
    },
    {
      id: 'Partition / Taqseem',
      label: 'Partition / Taqseem',
      shortLabel: 'Partition',
      description: 'Proceedings for partition/separation of joint land, preparation of shares, mode of partition, objections, final partition, Sanad Taqseem, or possession after partition.',
      legalBasis: [
        'Land Revenue Act partition framework.',
        'Section 135 and related provisions for application for partition by joint owner/co-sharer, where applicable.',
        'Partition may involve questions of title; revenue officer may proceed or refer/leave title dispute depending law/practice.',
        'Civil court route may apply where title/ownership declaration is central.'
      ],
      subtypes: [
        'Partition of Joint Khata',
        'Partition of Agricultural Land',
        'Partition of Inherited Land',
        'Preparation of Shares',
        'Objection to Mode of Partition',
        'Challenge to Partition Proceedings',
        'Final Partition / Sanad Taqseem',
        'Possession After Partition',
        'Other Partition Matter',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Assistant Collector / Revenue Officer',
          label: 'Assistant Collector / Revenue Officer',
          description: 'Common forum for partition proceedings under land revenue law.'
        },
        {
          id: 'Tehsildar / Naib Tehsildar',
          label: 'Tehsildar / Naib Tehsildar',
          description: 'Use where local setup assigns partition steps or reporting to this authority.'
        },
        {
          id: 'Collector',
          label: 'Collector',
          description: 'For appeal/revision/administrative control depending law/practice.'
        },
        {
          id: 'Commissioner',
          label: 'Commissioner',
          description: 'For appellate/revisional revenue forum where applicable.'
        },
        {
          id: 'Board of Revenue',
          label: 'Board of Revenue',
          description: 'For higher revision/review/reference where applicable.'
        },
        {
          id: 'Civil Court',
          label: 'Civil Court',
          description: 'Use where title, ownership share, declaration, or civil rights require adjudication.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where partition/revenue order is challenged by writ where maintainable.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Partition forum depends on joint revenue record, co-sharer status, title dispute, objections, and stage of partition proceedings.',
      sideOptions: ['Applicant', 'Respondent', 'Co-Sharer', 'Objector', 'Landowner', 'Shareholder'],
      documentTabs: [
        'All',
        'Partition Application',
        'Revenue Record',
        'Mode of Partition',
        'Objections / Replies',
        'Orders / Sanad Taqseem',
        'Miscellaneous'
      ],
      defaultStages: [
        'Joint Record / Share Review',
        'Partition Application Drafting',
        'Filing before Revenue Officer',
        'Notice to Co-Sharers',
        'Objections by Co-Sharers, if any',
        'Determination of Shares',
        'Preparation of Mode of Partition',
        'Objections to Mode of Partition',
        'Approval / Modification of Mode',
        'Final Partition / Sanad Taqseem',
        'Possession / Implementation',
        'Appeal / Revision / Writ, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand joint khata, co-sharers, shares, khasra numbers, mode of partition, objections, title dispute, Sanad Taqseem, and possession after partition.'
    },
    {
      id: 'Correction of Revenue Record',
      label: 'Correction of Revenue Record',
      shortLabel: 'Record Correction',
      description: 'Proceedings for correction of Jamabandi, Khasra Girdawari, Khatauni, Khewat, ownership entries, possession entries, name, parentage, area, khasra number, share, or other revenue record mistakes.',
      legalBasis: [
        'Land Revenue Act record-of-rights and periodical record framework.',
        'Correction of revenue entries may be handled by revenue authorities where within their jurisdiction.',
        'Civil court may be required where correction depends on declaration of title or complex civil rights.',
        'Khasra Girdawari correction often concerns possession/cultivation entries and local revenue inquiry.'
      ],
      subtypes: [
        'Correction of Jamabandi',
        'Correction of Khasra Girdawari',
        'Correction of Khatauni / Khewat',
        'Correction of Ownership Entry',
        'Correction of Possession Entry',
        'Correction of Name / Parentage',
        'Correction of Area / Khasra Number',
        'Correction of Share',
        'Removal of Wrong Entry',
        'Fraudulent Revenue Entry',
        'Other Record Correction',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Revenue Officer / Assistant Collector',
          label: 'Revenue Officer / Assistant Collector',
          description: 'Common forum for correction of revenue record where within revenue jurisdiction.'
        },
        {
          id: 'Tehsildar / Naib Tehsildar',
          label: 'Tehsildar / Naib Tehsildar',
          description: 'Use where correction process is handled locally or administratively.'
        },
        {
          id: 'Service Delivery Centre / Land Record Centre',
          label: 'Service Delivery Centre / Land Record Centre',
          description: 'Use for administrative correction/fard/record services where applicable.'
        },
        {
          id: 'Collector',
          label: 'Collector',
          description: 'Use for higher revenue authority correction or appeal/revision route where applicable.'
        },
        {
          id: 'Board of Revenue',
          label: 'Board of Revenue',
          description: 'Use for higher administrative/revisional correction where applicable.'
        },
        {
          id: 'Civil Court',
          label: 'Civil Court',
          description: 'Use where correction requires declaration of title or adjudication of civil rights.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where revenue authority order/action is challenged by writ where maintainable.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Record correction forum depends on whether the error is clerical/administrative, possession/cultivation-related, disputed title, or challenge to an order.',
      sideOptions: ['Applicant', 'Respondent', 'Petitioner', 'Landowner', 'Objector', 'Recorded Owner', 'Possessor'],
      documentTabs: [
        'All',
        'Correction Application',
        'Revenue Record',
        'Supporting Documents',
        'Objections / Replies',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Record Error Identification',
        'Revenue Record Collection',
        'Correction Application Drafting',
        'Filing / Submission',
        'Field Report / Patwari Report',
        'Notice to Affected Parties, if required',
        'Hearing / Inquiry',
        'Objections / Reply, if any',
        'Correction Order / Rejection',
        'Implementation in Record',
        'Appeal / Revision / Civil Remedy, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand wrong entry, correct entry sought, old/current record, basis of correction, mutation/deed/decree, possession proof, affected parties, and implementation.'
    },
    {
      id: 'Demarcation / Measurement',
      label: 'Demarcation / Measurement',
      shortLabel: 'Demarcation',
      description: 'Proceedings for demarcation, measurement, boundary identification, khasra location, encroachment identification, local inspection, tatima/site plan issue, or rasta/pathway demarcation.',
      legalBasis: [
        'Land revenue field measurement and record framework.',
        'Demarcation is commonly based on revenue record, shajra/map, khasra numbers, and field measurement.',
        'Boundary/title disputes may require civil court if revenue demarcation cannot resolve the legal issue.'
      ],
      subtypes: [
        'Demarcation of Land',
        'Measurement of Land',
        'Boundary Dispute',
        'Identification of Khasra Number',
        'Encroachment Identification',
        'Local Inspection',
        'Tatima / Site Plan Issue',
        'Pathway / Rasta Demarcation',
        'Other Demarcation Matter',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Revenue Officer / Assistant Collector',
          label: 'Revenue Officer / Assistant Collector',
          description: 'Common forum for demarcation/measurement based on revenue record.'
        },
        {
          id: 'Tehsildar / Naib Tehsildar',
          label: 'Tehsildar / Naib Tehsildar',
          description: 'Use where local revenue authority supervises measurement/demarcation.'
        },
        {
          id: 'Patwari / Qanoongo / Girdawar',
          label: 'Patwari / Qanoongo / Girdawar',
          description: 'For field report, measurement, site plan, or record-based assistance where applicable.'
        },
        {
          id: 'Civil Court',
          label: 'Civil Court',
          description: 'Use where demarcation is linked to civil injunction, possession, title, or encroachment dispute requiring civil adjudication.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where revenue authority failure/order is challenged by writ where maintainable.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Demarcation forum depends on whether the issue is simple measurement, revenue record correction, encroachment, possession, or title dispute.',
      sideOptions: ['Applicant', 'Respondent', 'Landowner', 'Neighboring Owner', 'Objector', 'Alleged Encroacher'],
      documentTabs: [
        'All',
        'Demarcation Application',
        'Revenue Record / Maps',
        'Field Report',
        'Objections / Replies',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Land Record / Map Review',
        'Demarcation Application Drafting',
        'Filing / Submission',
        'Notice to Neighboring Owners / Interested Parties',
        'Field Staff Appointment / Direction',
        'Site Inspection / Measurement',
        'Field Report Submitted',
        'Objections to Report, if any',
        'Hearing',
        'Demarcation Order / Report Confirmation',
        'Implementation / Further Remedy',
        'Closed'
      ],
      aiContextHints: 'AI should understand khasra numbers, boundaries, shajra/map, neighboring owners, encroachment allegation, field report, objections, and requested demarcation.'
    },
    {
      id: 'Land Possession / Dispossession',
      label: 'Land Possession / Dispossession',
      shortLabel: 'Possession',
      description: 'Proceedings relating to possession, illegal dispossession, interference, encroachment, restoration of possession, or possession disputes connected with revenue record.',
      legalBasis: [
        'Revenue record possession entries may support possession issues.',
        'Civil court may be appropriate for declaration/injunction/possession suits.',
        'Illegal dispossession may involve special statute/criminal/administrative route depending facts.',
        'Revenue forum may be relevant where issue is record-based possession or administrative demarcation/encroachment.'
      ],
      subtypes: [
        'Illegal Dispossession',
        'Restoration of Possession',
        'Interference with Possession',
        'Encroachment',
        'Possession According to Revenue Record',
        'Dispossession by Co-Sharer',
        'Dispossession by Stranger',
        'Police / Administrative Possession Matter',
        'Other Possession Matter',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Civil Court',
          label: 'Civil Court',
          description: 'Common forum for possession, declaration, injunction, and civil rights disputes.'
        },
        {
          id: 'Revenue Officer / Assistant Collector',
          label: 'Revenue Officer / Assistant Collector',
          description: 'Use where possession issue is tied to revenue record, partition implementation, or administrative revenue proceedings.'
        },
        {
          id: 'District Administration / Police',
          label: 'District Administration / Police',
          description: 'Use where immediate administrative/police protection or illegal dispossession route is relevant.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where administrative inaction/order is challenged by writ where maintainable.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Possession forum depends on whether the dispute is civil title/possession, revenue record implementation, illegal dispossession, encroachment, or administrative action.',
      sideOptions: ['Applicant', 'Respondent', 'Landowner', 'Occupant', 'Co-Sharer', 'Alleged Encroacher', 'Dispossessed Party'],
      documentTabs: [
        'All',
        'Possession Claim / Application',
        'Revenue Record',
        'Possession Evidence',
        'Applications',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Possession / Record Review',
        'Incident / Encroachment Evidence Collection',
        'Claim / Application Drafting',
        'Filing before Relevant Forum',
        'Urgent Relief / Protection, if any',
        'Notice to Respondent',
        'Reply / Objections',
        'Demarcation / Field Report, if required',
        'Evidence / Hearing',
        'Order / Judgment',
        'Implementation / Restoration / Removal',
        'Further Remedy, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand current possession, dispossession date, accused encroacher, revenue entries, khasra numbers, possession proof, demarcation report, police/administrative steps, and relief sought.'
    },
    {
      id: 'Revenue Appeal',
      label: 'Revenue Appeal',
      shortLabel: 'Appeal',
      description: 'Appeal against an order passed by revenue officer, Assistant Collector, Collector, Commissioner, or other revenue authority where appeal is maintainable.',
      legalBasis: [
        'Land Revenue Act appeal framework.',
        'Appeal forum depends on the authority passing the original order.',
        'Limitation and forum may vary by statute/province/order type.',
        'Commonly involves mutation, partition, record correction, demarcation, or other revenue order.'
      ],
      subtypes: [
        'Appeal Against Mutation Order',
        'Appeal Against Partition Order',
        'Appeal Against Record Correction Order',
        'Appeal Against Demarcation Order',
        'Appeal Against Collector Order',
        'Appeal Against Assistant Collector Order',
        'Appeal Against Commissioner Order',
        'Appeal Against Revenue Officer Order',
        'Other Revenue Appeal',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Collector',
          label: 'Collector',
          description: 'Appellate forum for certain revenue officer/Assistant Collector orders where applicable.'
        },
        {
          id: 'Commissioner',
          label: 'Commissioner',
          description: 'Appellate/revisional forum for certain Collector/revenue orders where applicable.'
        },
        {
          id: 'Board of Revenue',
          label: 'Board of Revenue',
          description: 'Higher revenue forum for appeal/revision/review where applicable.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where revenue order is challenged by writ where maintainable.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Revenue appeal forum depends on the officer who passed the impugned order, statute, limitation, and local revenue hierarchy.',
      sideOptions: ['Appellant', 'Respondent'],
      documentTabs: [
        'All',
        'Memo of Appeal',
        'Applications',
        'Impugned Revenue Order',
        'Revenue Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Impugned Order Review',
        'Certified Copy / Record Collection',
        'Memo of Appeal Drafting',
        'Filing',
        'Scrutiny / Objections, if any',
        'Admission / Preliminary Hearing',
        'Notice to Respondent',
        'Record Summoned / Received',
        'Stay Application, if any',
        'Arguments',
        'Appellate Order',
        'Revision / Writ / Further Remedy, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand impugned revenue order, lower forum, limitation, grounds of appeal, record, stay, and relief sought.'
    },
    {
      id: 'Revenue Revision',
      label: 'Revenue Revision',
      shortLabel: 'Revision',
      description: 'Revision against revenue orders or proceedings before higher revenue authority, Commissioner, Board of Revenue, or competent revisional forum.',
      legalBasis: [
        'Land Revenue Act revision/control framework.',
        'Revisional forum depends on impugned order, hierarchy, and applicable law.',
        'Revision generally focuses on legality, jurisdiction, irregularity, or improper exercise of revenue authority.'
      ],
      subtypes: [
        'Revision Against Mutation Order',
        'Revision Against Partition Order',
        'Revision Against Appeal Order',
        'Revision Against Collector Order',
        'Revision Against Commissioner Order',
        'Revision Against Board of Revenue Order',
        'Revision for Jurisdictional Error',
        'Revision for Illegal Revenue Entry',
        'Other Revenue Revision',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Commissioner',
          label: 'Commissioner',
          description: 'Revisional forum for certain revenue orders where applicable.'
        },
        {
          id: 'Board of Revenue',
          label: 'Board of Revenue',
          description: 'Higher revisional/review forum in revenue matters where applicable.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where writ challenge is maintainable.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Revenue revision forum depends on the impugned order, revenue hierarchy, statutory remedy, and whether writ jurisdiction is maintainable.',
      sideOptions: ['Petitioner', 'Respondent', 'Applicant', 'Objector'],
      documentTabs: [
        'All',
        'Revision Petition',
        'Applications',
        'Impugned Order / Record',
        'Revenue Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Impugned Order / Record Review',
        'Maintainability / Forum Review',
        'Revision Petition Drafting',
        'Filing',
        'Scrutiny / Objections, if any',
        'Preliminary Hearing',
        'Notice to Respondent',
        'Record Called, if required',
        'Stay / Suspension Application, if any',
        'Arguments',
        'Final Revisional Order',
        'Further Remedy / Writ, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand impugned order, revenue hierarchy, jurisdictional error, illegality, material irregularity, record, and relief sought.'
    },
    {
      id: 'Board of Revenue Matter',
      label: 'Board of Revenue Matter',
      shortLabel: 'Board of Revenue',
      description: 'Proceedings before Board of Revenue or senior revenue authority, including revision, review, reference, appeal, administrative correction, or revenue record dispute.',
      legalBasis: [
        'Board of Revenue is the highest provincial revenue authority for many revenue administrative/revisional matters.',
        'Maintainability depends on statute, hierarchy, order type, and province.',
        'Board matters may include revision, review, reference, administrative correction, or transfer/control.'
      ],
      subtypes: [
        'Board of Revenue Revision',
        'Board of Revenue Review',
        'Board of Revenue Appeal',
        'Board of Revenue Reference',
        'Challenge to Commissioner Order',
        'Challenge to Collector Order',
        'Administrative Revenue Correction',
        'Other Board of Revenue Matter',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Board of Revenue',
          label: 'Board of Revenue',
          description: 'Primary forum for Board of Revenue matters.'
        },
        {
          id: 'Member Board of Revenue',
          label: 'Member Board of Revenue',
          description: 'Use where matter is before a Member, Board of Revenue.'
        },
        {
          id: 'Senior Member Board of Revenue',
          label: 'Senior Member Board of Revenue',
          description: 'Use where matter is before Senior Member Board of Revenue, depending province/practice.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where Board order is challenged through writ/constitutional jurisdiction.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Board of Revenue forum depends on province, case type, statutory remedy, and internal Board jurisdiction.',
      sideOptions: ['Petitioner', 'Respondent', 'Applicant', 'Objector', 'Landowner'],
      documentTabs: [
        'All',
        'Board Petition / Application',
        'Revenue Record',
        'Impugned Orders',
        'Applications',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Lower Revenue Record Review',
        'Board Remedy / Maintainability Review',
        'Petition / Application Drafting',
        'Filing before Board of Revenue',
        'Scrutiny / Objections, if any',
        'Preliminary Hearing',
        'Notice to Respondents',
        'Record Called, if required',
        'Stay / Interim Relief, if any',
        'Arguments',
        'Board Order',
        'Implementation / Writ / Further Remedy',
        'Closed'
      ],
      aiContextHints: 'AI should understand lower revenue orders, Commissioner/Collector order, Board jurisdiction, revision/review/reference, administrative correction, and relief sought.'
    },
    {
      id: 'Land Acquisition / Compensation',
      label: 'Land Acquisition / Compensation',
      shortLabel: 'Land Acquisition',
      description: 'Proceedings relating to acquisition of land, compensation, award, apportionment, enhancement, reference, possession, or challenge to acquisition process.',
      legalBasis: [
        'Land Acquisition Act, 1894 / applicable acquisition law.',
        'Collector / Land Acquisition Collector award and compensation process.',
        'Reference to court for compensation/enhancement/apportionment where applicable.',
        'Constitutional/writ challenge may be relevant for acquisition process, public purpose, or illegal possession.'
      ],
      subtypes: [
        'Challenge to Acquisition',
        'Compensation Enhancement',
        'Apportionment of Compensation',
        'Reference Against Award',
        'Objection to Award',
        'Possession / Taking Over',
        'Non-Payment of Compensation',
        'Market Value Dispute',
        'Other Acquisition Matter',
        'Other'
      ],
      forumOptions: [
        {
          id: 'Land Acquisition Collector',
          label: 'Land Acquisition Collector',
          description: 'Authority for acquisition award, compensation assessment, and acquisition proceedings.'
        },
        {
          id: 'Reference Court / District Court',
          label: 'Reference Court / District Court',
          description: 'Forum for reference/enhancement/apportionment disputes where applicable.'
        },
        {
          id: 'Collector / Deputy Commissioner',
          label: 'Collector / Deputy Commissioner',
          description: 'Use for acquisition administration, possession, and compensation process where applicable.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'Use where acquisition process/order/action is challenged by writ where maintainable.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Land acquisition forum depends on award stage, compensation dispute, reference, apportionment, possession, and whether acquisition legality is challenged.',
      sideOptions: ['Landowner / Claimant', 'Acquiring Authority', 'Respondent', 'Applicant', 'Objector'],
      documentTabs: [
        'All',
        'Acquisition Claim / Reference',
        'Award / Notifications',
        'Land Record',
        'Compensation Record',
        'Applications',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Acquisition Notice / Award Review',
        'Land Record / Ownership Review',
        'Objection / Reference / Claim Drafting',
        'Filing before Competent Forum',
        'Notice to Authority / Parties',
        'Record / Award Produced',
        'Evidence of Market Value / Entitlement',
        'Arguments',
        'Award / Reference Decision / Order',
        'Compensation Payment / Apportionment',
        'Further Remedy / Writ, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand acquired land, award number/date, compensation amount, market value, ownership/share, apportionment, possession, acquisition notices, beneficiary department, and relief sought.'
    },
    {
      id: 'Revenue Miscellaneous Application',
      label: 'Revenue Miscellaneous Application',
      shortLabel: 'Application',
      description: 'Connected or standalone applications in revenue/land proceedings, including stay, interim relief, restoration, condonation of delay, implementation, transfer, inspection, record summoning, or correction-related applications.',
      subtypes: [
        'Stay Application',
        'Interim Relief Application',
        'Application for Restoration',
        'Application for Condonation of Delay',
        'Application for Summoning Revenue Record',
        'Application for Local Inspection',
        'Application for Implementation of Order',
        'Application for Transfer',
        'Application for Review',
        'Application for Correction',
        'Application for Early Hearing',
        'Other Revenue Application',
        'Other'
      ],
      legalBasis: [],
      forumOptions: [
        {
          id: 'Same Revenue Forum Where Case Is Pending',
          label: 'Same Revenue Forum Where Case Is Pending',
          description: 'For applications connected with pending revenue proceedings.'
        },
        {
          id: 'Revenue Officer / Assistant Collector',
          label: 'Revenue Officer / Assistant Collector',
          description: 'For applications before local revenue forum.'
        },
        {
          id: 'Collector',
          label: 'Collector',
          description: 'For applications connected with appeal/administrative revenue proceedings.'
        },
        {
          id: 'Commissioner',
          label: 'Commissioner',
          description: 'For applications connected with appeal/revision before Commissioner.'
        },
        {
          id: 'Board of Revenue',
          label: 'Board of Revenue',
          description: 'For applications connected with Board proceedings.'
        },
        {
          id: 'High Court / Constitutional Jurisdiction',
          label: 'High Court / Constitutional Jurisdiction',
          description: 'For applications connected with writ challenge.'
        },
        {
          id: 'Other / Custom Forum',
          label: 'Other / Custom Forum',
          description: 'Use if forum differs.'
        }
      ],
      forumHelper: 'Application forum depends on the main revenue matter, pending forum, and nature of application.',
      sideOptions: ['Applicant', 'Respondent', 'Petitioner', 'Objector', 'Landowner'],
      documentTabs: [
        'All',
        'Application',
        'Reply / Objections',
        'Supporting Documents',
        'Revenue Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Application Preparation',
        'Filing',
        'Scrutiny / Objections, if any',
        'Notice to Respondent, if required',
        'Reply / Objections',
        'Record / Report Called, if required',
        'Arguments / Hearing',
        'Order',
        'Compliance / Main Case Linkage',
        'Closed'
      ],
      aiContextHints: 'AI should understand main revenue case, application subtype, pending forum, khasra/khewat/mutation/order details, urgency, record needed, and order sought.'
    },
    {
      id: 'Other Revenue / Land Matter',
      label: 'Other Revenue / Land Matter',
      shortLabel: 'Other Revenue',
      description: 'Use this for revenue, land record, agricultural land, property record, or land administration matters that do not fit the available revenue case types.',
      legalBasis: [],
      subtypes: [
        'Custom Revenue Matter',
        'Custom Land Matter',
        'Other'
      ],
      forumOptions: [
        { id: 'Revenue Officer / Assistant Collector', label: 'Revenue Officer / Assistant Collector', description: '' },
        { id: 'Tehsildar / Naib Tehsildar', label: 'Tehsildar / Naib Tehsildar', description: '' },
        { id: 'Collector', label: 'Collector', description: '' },
        { id: 'Commissioner', label: 'Commissioner', description: '' },
        { id: 'Board of Revenue', label: 'Board of Revenue', description: '' },
        { id: 'Civil Court', label: 'Civil Court', description: '' },
        { id: 'High Court / Constitutional Jurisdiction', label: 'High Court / Constitutional Jurisdiction', description: '' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: '' }
      ],
      sideOptions: ['Party A', 'Party B', 'Applicant', 'Respondent', 'Landowner', 'Objector'],
      documentTabs: [
        'All',
        'Documents',
        'Applications',
        'Revenue Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Case Preparation / Record Review',
        'Filing / Institution',
        'Notice / Report, if any',
        'Reply / Objections',
        'Record / Evidence / Field Report',
        'Arguments / Hearing',
        'Order / Judgment',
        'Implementation / Further Remedy',
        'Closed'
      ],
      aiContextHints: 'AI should rely on user-provided description, revenue record, land details, forum, uploaded documents, and selected relief.'
    }
  ]
};

export type RevenueProvince = 'KPK' | 'Punjab' | 'Sindh' | 'Balochistan' | 'AJK' | 'GB';

export const REVENUE_PROVINCE_OPTIONS: RevenueProvince[] = [
  'KPK',
  'Punjab',
  'Sindh',
  'Balochistan',
  'AJK',
  'GB',
];

export const REVENUE_PROVINCE_FORUMS: Record<RevenueProvince, string[]> = {
  KPK: [
    'Tehsildar',
    'Assistant Collector Grade-II',
    'Assistant Collector Grade-I',
    'Collector',
    'Commissioner',
    'Board of Revenue KPK',
    'High Court (Peshawar)',
    'Custom',
  ],
  Punjab: [
    'Tehsildar',
    'Assistant Collector',
    'Collector',
    'Additional Commissioner',
    'Commissioner',
    'Board of Revenue Punjab',
    'High Court (Lahore)',
    'Custom',
  ],
  Sindh: [
    'Mukhtiarkar',
    'Assistant Collector',
    'Collector',
    'Commissioner',
    'Board of Revenue Sindh',
    'High Court (Karachi)',
    'Custom',
  ],
  Balochistan: [
    'Tehsildar',
    'Assistant Collector',
    'Collector',
    'Commissioner',
    'Board of Revenue Balochistan',
    'High Court (Quetta)',
    'Custom',
  ],
  AJK: [
    'Tehsildar',
    'Collector',
    'Commissioner',
    'High Court AJK',
    'Custom',
  ],
  GB: [
    'Tehsildar',
    'Assistant Collector',
    'Collector',
    'Commissioner',
    'Chief Court GB',
    'Custom',
  ],
};

export const getRevenueLandCaseTypes = () => REVENUE_LAND_CASE_TAXONOMY_CONFIG.types;

export const getRevenueLandSubtypes = (caseTypeId: string) => {
  const type = REVENUE_LAND_CASE_TAXONOMY_CONFIG.types.find((t) => t.id === caseTypeId);
  return type ? type.subtypes : [];
};

export const normalizeRevenueLandCaseClassification = (existingCase: any) => {
  // If it's already well-formed Revenue/Land case, return it
  if (existingCase.caseCategory === 'Revenue / Land') {
    return {
      caseCategory: 'Revenue / Land',
      caseType: existingCase.caseType || '',
      caseSubtype: existingCase.caseSubtype || '',
      primaryRelief: existingCase.primaryRelief || '',
    };
  }

  const legacyType = existingCase.matterType || existingCase.caseTypeName || existingCase.caseType || '';
  const lowerType = String(legacyType).toLowerCase();
  
  // Also check title and description for some keywords
  const lowerTitle = String(existingCase.title || '').toLowerCase();
  const lowerDesc = String(existingCase.description || '').toLowerCase();
  
  let caseCategory = '';
  let caseType = '';
  let caseSubtype = '';

  const isMatch = (arr: string[], str: string) => arr.some(keyword => str.includes(keyword.toLowerCase()));

  // 1. Mutation Matter
  if (isMatch(['mutation case', 'mutation matter'], lowerType)) {
    caseCategory = 'Revenue / Land';
    caseType = 'Mutation Matter';
  } else if (isMatch(['attestation of mutation'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Mutation Matter'; caseSubtype = 'Attestation of Mutation';
  } else if (isMatch(['challenge to mutation'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Mutation Matter'; caseSubtype = 'Challenge to Mutation';
  } else if (isMatch(['cancellation of mutation'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Mutation Matter'; caseSubtype = 'Cancellation of Mutation';
  } else if (isMatch(['correction of mutation'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Mutation Matter'; caseSubtype = 'Correction of Mutation';
  } else if (isMatch(['inheritance mutation'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Mutation Matter'; caseSubtype = 'Inheritance Mutation';
  } else if (isMatch(['sale mutation', 'gift mutation'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Mutation Matter'; 
    caseSubtype = lowerType.includes('sale') ? 'Sale Mutation' : 'Gift Mutation';
  }
  
  // 2. Partition / Taqseem
  else if (isMatch(['partition case', 'taqseem case'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Partition / Taqseem';
  } else if (isMatch(['partition of joint khata'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Partition / Taqseem'; caseSubtype = 'Partition of Joint Khata';
  } else if (isMatch(['sanad taqseem', 'final partition', 'sanad-e-taqseem'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Partition / Taqseem'; caseSubtype = 'Final Partition / Sanad Taqseem';
  }

  // 3. Correction of Revenue Record
  else if (isMatch(['correction of revenue record'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Correction of Revenue Record';
  } else if (isMatch(['correction of jamabandi'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Correction of Revenue Record'; caseSubtype = 'Correction of Jamabandi';
  } else if (isMatch(['correction of khasra girdawari'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Correction of Revenue Record'; caseSubtype = 'Correction of Khasra Girdawari';
  } else if (isMatch(['correction of khewat'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Correction of Revenue Record'; caseSubtype = 'Correction of Khatauni / Khewat';
  } else if (isMatch(['wrong entry'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Correction of Revenue Record'; caseSubtype = 'Removal of Wrong Entry';
  }
  
  // 4. Demarcation / Measurement
  else if (isMatch(['demarcation case', 'measurement case'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Demarcation / Measurement';
  } else if (isMatch(['boundary dispute'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Demarcation / Measurement'; caseSubtype = 'Boundary Dispute';
  } else if (isMatch(['encroachment identification'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Demarcation / Measurement'; caseSubtype = 'Encroachment Identification';
  }
  
  // 5. Land Possession / Dispossession
  else if (isMatch(['illegal dispossession of land'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Land Possession / Dispossession'; caseSubtype = 'Illegal Dispossession';
  } else if (isMatch(['land possession case'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Land Possession / Dispossession';
  } else if (isMatch(['revenue encroachment'], lowerType) || (lowerType === 'encroachment' && (lowerTitle.includes('revenue') || lowerTitle.includes('land')))) {
    caseCategory = 'Revenue / Land'; caseType = 'Land Possession / Dispossession'; caseSubtype = 'Encroachment';
  }
  
  // 6. Revenue Appeal
  else if (isMatch(['revenue appeal'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Appeal';
  } else if (isMatch(['appeal against mutation order'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Appeal'; caseSubtype = 'Appeal Against Mutation Order';
  } else if (isMatch(['appeal against partition order'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Appeal'; caseSubtype = 'Appeal Against Partition Order';
  } else if (isMatch(['appeal against collector order'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Appeal'; caseSubtype = 'Appeal Against Collector Order';
  }

  // 7. Revenue Revision
  else if (isMatch(['revenue revision'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Revision';
  } else if (isMatch(['revision against mutation order'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Revision'; caseSubtype = 'Revision Against Mutation Order';
  } else if (isMatch(['revision against collector order'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Revision'; caseSubtype = 'Revision Against Collector Order';
  }

  // 8. Board of Revenue Matter
  else if (isMatch(['board of revenue case', 'bor case'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Board of Revenue Matter';
  } else if (isMatch(['board of revenue revision'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Board of Revenue Matter'; caseSubtype = 'Board of Revenue Revision';
  } else if (isMatch(['board of revenue review'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Board of Revenue Matter'; caseSubtype = 'Board of Revenue Review';
  }

  // 9. Land Acquisition / Compensation
  else if (isMatch(['land acquisition'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Land Acquisition / Compensation';
  } else if (isMatch(['compensation enhancement'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Land Acquisition / Compensation'; caseSubtype = 'Compensation Enhancement';
  } else if (isMatch(['reference against award'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Land Acquisition / Compensation'; caseSubtype = 'Reference Against Award';
  } else if (isMatch(['objection to award'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Land Acquisition / Compensation'; caseSubtype = 'Objection to Award';
  }

  // 10. Revenue Miscellaneous Application
  else if (isMatch(['revenue miscellaneous application'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Miscellaneous Application';
  } else if (isMatch(['stay in revenue case'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Miscellaneous Application'; caseSubtype = 'Stay Application';
  } else if (isMatch(['summoning revenue record'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Miscellaneous Application'; caseSubtype = 'Application for Summoning Revenue Record';
  } else if (isMatch(['local inspection revenue'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Revenue Miscellaneous Application'; caseSubtype = 'Application for Local Inspection';
  }

  // 11. Other Revenue / Land Matter
  else if (isMatch(['revenue case', 'land revenue case'], lowerType)) {
    caseCategory = 'Revenue / Land'; caseType = 'Other Revenue / Land Matter';
  }

  if (caseCategory) {
    return {
      caseCategory,
      caseType,
      ...(caseSubtype && { caseSubtype }),
      legacyCaseType: legacyType,
    };
  }

  return { ...existingCase }; // Could not be mapped as Revenue / Land
};
